import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { BackendFilters } from "../../utils/backendFilters";
import DateRangePicker, { type DateRange } from "../DateRangePicker";
import PaymentsTable from "../PaymentsTable";

export interface PaymentDashboardSettings {
  selectedDate: DateRange;
}

interface PaymentsListViewProps {
  payments: PaymentMessage[];
  paymentsLoading: boolean;
  selectedPractices: PracticeMessage[];
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
  dashboardSettings: PaymentDashboardSettings;
  saveDashboardSettings: (newSettings: PaymentDashboardSettings) => void;
  onSavePayment: (payment: PaymentMessage, file: File | null) => Promise<void>;
  onSaveAI: (aiMessage: AIMessage, file: File) => Promise<void>;
  validatePayment?: (payment: PaymentMessage) => void;
  onFilterChange?: (filters: BackendFilters) => void;
  pagination?: {
    page: number;
    pageSize: number;
    rowCount: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

export default function PaymentsListView({
  payments,
  paymentsLoading,
  selectedPractices,
  supportedPayers,
  supportedSources,
  dashboardSettings,
  saveDashboardSettings,
  onSavePayment,
  onSaveAI,
  validatePayment,
  onFilterChange,
  pagination,
}: PaymentsListViewProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleRowClick = (payment: PaymentMessage | null) => {
    if (payment) {
      navigate({
        pathname: `/payments/${payment.wieldyId}`,
        search: searchParams.toString(),
      });
    }
  };

  const onDateChange = (dateRange: DateRange) => {
    saveDashboardSettings({ ...dashboardSettings, selectedDate: dateRange });
  };

  const sortedPayments = [...payments].sort((a, b) =>
    dayjs(a.paymentDate).isAfter(dayjs(b.paymentDate)) ? -1 : 1,
  );

  return (
    <>
      <h1>Payments</h1>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "14px",
        }}
      >
        <div>
          <DateRangePicker
            selectedDateRange={dashboardSettings.selectedDate}
            setDateRange={onDateChange}
          />
        </div>
      </div>
      <PaymentsTable
        practices={selectedPractices}
        supportedPayers={supportedPayers}
        supportedSources={supportedSources}
        isLoading={paymentsLoading}
        payments={sortedPayments}
        onSelectPayment={handleRowClick}
        persistKey="paymentsListView"
        onSavePayment={onSavePayment}
        onSaveAI={onSaveAI}
        validatePayment={validatePayment}
        onFilterChange={onFilterChange}
        pagination={pagination}
      />
    </>
  );
}
